import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import ButtonTsoPq from '@components/Buttons/ButtonTsoPq';
import IconButtonTsoPq from '@components/Buttons/IconButtonTsoPq';
import TypographyTsoPq from '@components/Typography/TypographyTsoPq';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { Box, styled } from '@mui/material';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyledConfirmationHeader, StyledHeader, StyledTitle, StyledTitleWrapper } from './StyledMasterdataModal';

const StyledPageWrapper = styled(Box)<{ $customWidth?: string }>(({ $customWidth = '47.125rem' }) => ({
  width: $customWidth,
  padding: 0,
  maxHeight: '44.375rem',
}));

const StyledConfirmationModalWrapper = styled(Box)({
  maxWidth: '43.1875rem',
  display: 'flex',
  flexDirection: 'column',
  gap: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_24,
});

export const StyledActionSection = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
});

export const StyledButtonsWrapper = styled(Box)({
  display: 'flex',
  gap: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_16,
});

type FormModalWrapperProps = {
  show: boolean;
  newItem?: boolean;
  title: string;
  titleEdit: string;
  onSubmit: (data: any) => void;
  onDiscard?: () => void;
  defaultValues?: Record<string, any>;
  showDeleteButton?: boolean;
  deleteButtonDisabled?: boolean;
  formContent: (register: any, watch: any, setValue: any, errors: any) => ReactNode;
  customWidth?: string;
  formComponent: React.ComponentType<any>;
  formProps?: any;
  tooltipText?: string;
  onDeleteConfirm?: () => void;
  onCancel?: () => void;
  deleteTitle?: string;
  deleteText?: string;
};

type DeleteConfirmationModalProps = {
  show: boolean;
  title: string;
  text: string;
  onConfirm: () => void;
  onCancel: () => void;
};

const FormModalWrapper = ({
  show,
  newItem,
  title,
  titleEdit,
  onSubmit,
  onDiscard,
  defaultValues = {},
  showDeleteButton = false,
  formContent,
  customWidth,
  formComponent: FormComponent,
  formProps = {},
  deleteButtonDisabled = false,
  tooltipText = '',
  onDeleteConfirm,
  onCancel,
  deleteTitle,
  deleteText,
}: FormModalWrapperProps) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  if (!show) return null;

  if (showDeleteModal) {
    return (
      <DeleteConfirmationModal
        show={true}
        title={deleteTitle ?? ''}
        text={deleteText ?? ''}
        onConfirm={() => {
          onDeleteConfirm?.();
          setShowDeleteModal(false);
          onDiscard?.();
        }}
        onCancel={() => {
          onCancel?.();
          setShowDeleteModal(false);
        }}
      />
    );
  }

  return (
    <StyledPageWrapper $customWidth={customWidth}>
      <StyledHeader>
        <StyledTitleWrapper>
          <StyledTitle>{newItem ? title : titleEdit}</StyledTitle>
          {showDeleteButton && (
            <IconButtonTsoPq
              sizeVariant={'l'}
              tooltip={tooltipText}
              buttonType="transparentPrimaryNoEffect"
              disabled={deleteButtonDisabled}
              icon={faTrash}
              onClick={(event) => {
                event.stopPropagation();
                setShowDeleteModal(true);
              }}
            />
          )}
        </StyledTitleWrapper>
      </StyledHeader>
      <FormComponent
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        discardChanges={onDiscard}
        newForm={newItem}
        stickyPosition={true}
        {...formProps}
      >
        {formContent}
      </FormComponent>
    </StyledPageWrapper>
  );
};
export const DeleteConfirmationModal = ({ show, title, text, onConfirm, onCancel }: DeleteConfirmationModalProps) => {
  const { t } = useTranslation();

  if (!show) return null;

  return (
    <StyledConfirmationModalWrapper>
      <StyledConfirmationHeader>
        <StyledTitle>
          <TypographyTsoPq typographyType="sectionTitleMDBold">{title}</TypographyTsoPq>{' '}
        </StyledTitle>
      </StyledConfirmationHeader>

      <TypographyTsoPq typographyType="bodyTextMD">{text}</TypographyTsoPq>

      <StyledActionSection>
        <StyledButtonsWrapper>
          <ButtonTsoPq
            buttonType="outlinedPrimary"
            onClick={onCancel}
            textContent={t('masterdata.cancel')}
          ></ButtonTsoPq>
          <ButtonTsoPq
            buttonType="filledPrimary"
            onClick={onConfirm}
            textContent={t('masterdata.delete')}
          ></ButtonTsoPq>
        </StyledButtonsWrapper>
      </StyledActionSection>
    </StyledConfirmationModalWrapper>
  );
};

export default FormModalWrapper;
